import { useEffect, useState } from 'react'

import Button from '@/components/Button'
import {
  useStatusPageSubscription,
  useSubscribeStatusPage,
  useUnsubscribeStatusPage,
} from '@/lib/queries/App.queries'
import { cn, parseResponseErrorsAndNotify } from '@/lib/misc/Utils'
import { GenericObject } from '@/lib/definitions/generic.types'

interface Props {
  isPopoverOpen: boolean
  userEmail: string
}

const StatusEmailSubscriptionBlock = ({ isPopoverOpen, userEmail }: Props) => {
  const [showSubscribeEmailConfirmation, setShowSubscribeEmailConfirmation] =
    useState(false)

  useEffect(() => {
    if (!isPopoverOpen) {
      setShowSubscribeEmailConfirmation(false)
    }
  }, [isPopoverOpen])

  const subscriptionQuery = useStatusPageSubscription(isPopoverOpen)
  const subscribeMutation = useSubscribeStatusPage()
  const unsubscribeMutation = useUnsubscribeStatusPage()

  const subscribed = subscriptionQuery.data?.subscribed === true
  const subscribePending = subscribeMutation.isPending
  const unsubscribePending = unsubscribeMutation.isPending
  const pending = subscribePending || unsubscribePending
  const loadError = subscriptionQuery.isError
  const canAct =
    subscriptionQuery.isSuccess ||
    subscribeMutation.isSuccess ||
    unsubscribeMutation.isSuccess

  const mutationActionError = subscribeMutation.isError
    ? subscribeMutation.error
    : null
  const mutationActionErrorFallback = unsubscribeMutation.isError
    ? unsubscribeMutation.error
    : null
  const actionErrorSource = mutationActionError ?? mutationActionErrorFallback

  let actionErrorMessage: string | null = null
  if (actionErrorSource != null) {
    const parsed = parseResponseErrorsAndNotify(
      actionErrorSource as GenericObject,
      undefined,
      false
    )
    actionErrorMessage =
      parsed != null && parsed.messages.length > 0
        ? parsed.messages.join(', ')
        : 'Something went wrong. Try again.'
  }

  const statusLine = loadError
    ? 'Could not load email preference.'
    : subscriptionQuery.isLoading
      ? 'Checking subscription…'
      : subscribed
        ? 'You get status updates by email.'
        : 'Get email for incidents and maintenance.'

  const showActionColumn = !loadError && (subscriptionQuery.isLoading || canAct)

  return (
    <div className="px-3 py-3 bg-neutral-10/75 rounded-lg m-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm text-neutral-60">{statusLine}</p>
          {userEmail.trim().length > 0 && (
            <p
              className="mt-0.5 truncate text-sm text-neutral-50"
              title={userEmail}
            >
              {userEmail}
            </p>
          )}
        </div>
        {showActionColumn && (
          <div className="flex shrink-0 justify-end">
            {subscriptionQuery.isLoading ? (
              <Button
                type="button"
                style="secondary"
                className="py-2 px-3 text-sm"
                disabled
              >
                Loading…
              </Button>
            ) : subscribed ? (
              <Button
                type="button"
                style="flat"
                className={cn(
                  'py-2 px-3 text-sm',
                  'border border-neutral-20 text-neutral-50 hover:bg-neutral-10'
                )}
                disabled={pending}
                onClick={() =>
                  unsubscribeMutation.mutate(undefined, {
                    onSuccess: () => setShowSubscribeEmailConfirmation(false),
                  })
                }
              >
                {unsubscribePending ? 'Unsubscribing…' : 'Unsubscribe'}
              </Button>
            ) : (
              <Button
                type="button"
                style="secondary"
                className="py-2 px-3 text-sm"
                disabled={pending}
                onClick={() =>
                  subscribeMutation.mutate(undefined, {
                    onSuccess: () => setShowSubscribeEmailConfirmation(true),
                  })
                }
              >
                {subscribePending ? 'Subscribing…' : 'Subscribe'}
              </Button>
            )}
          </div>
        )}
      </div>
      {showSubscribeEmailConfirmation && (
        <p className="mt-2 text-sm text-neutral-50" role="status">
          Check your email to confirm your subscription.
        </p>
      )}
      {actionErrorMessage != null && (
        <p className="mt-2 text-sm text-red-600" role="alert">
          {actionErrorMessage}
        </p>
      )}
    </div>
  )
}

export default StatusEmailSubscriptionBlock
